/* eslint-disable camelcase */
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQuery } from '@tanstack/react-query'

import AddPoToInvoiceForm from './components/AddPoToInvoiceForm'
import BulkConvertToSalesOwned from './components/BulkConvertToSalesOwned'
import BulkMoveToNewBilling from './components/BulkMoveToNewBilling'
import CommitResultPanel from './components/CommitResultPanel'
import ConvertToSalesOwnedForm from './components/ConvertToSalesOwnedForm'
import CostCenterPicker from './components/CostCenterPicker'
import NoInputForm from './components/NoInputForm'
import OperationList from './components/OperationList'
import PreviewPanel from './components/PreviewPanel'
import RecreateSubscriptionInvoiceForm from './components/RecreateSubscriptionInvoiceForm'
import SetInvoiceStatementDescriptorForm from './components/SetInvoiceStatementDescriptorForm'
import {
  AddPoToInvoiceInput,
  BillingOpsPreviewView,
  ConvertToSalesOwnedInput,
  ENGINEERING_ONLY_KINDS,
  OPERATION_KINDS,
  OperationInput,
  RecreateSubscriptionInvoiceInput,
  SetInvoiceStatementDescriptorInput,
} from './types'

import styles from './styles.module.scss'

import { isApiError } from '@/api'
import {
  commitBillingOp,
  listBillingOps,
  listCostCentersForEnterprise,
  listRecentInvoicesForCostCenter,
  previewBillingOp,
} from '@/api/billingOps'
import { CostCenterListItem, EnterpriseListItem } from '@/api/generated'
import EnterpriseSearch from '@/pages/app/Enterprises/components/EnterpriseSearch'
import { selectIsInternalAdmin } from '@/store/features/user/userSlice'

const FORM_ID = 'billing-ops-form'

const BillingOpsPage = () => {
  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const [enterprise, setEnterprise] = useState<EnterpriseListItem | null>(null)
  const [costCenter, setCostCenter] = useState<CostCenterListItem | null>(null)
  const [pickerError, setPickerError] = useState<string | null>(null)
  const [selectedKind, setSelectedKind] = useState<string | null>(null)
  const [preview, setPreview] = useState<BillingOpsPreviewView | null>(null)
  const [previewedInput, setPreviewedInput] = useState<OperationInput | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [commitError, setCommitError] = useState<string | null>(null)

  /**
   * Idempotency lifecycle: a single key per *commit attempt*. The same key is
   * reused across retries so Stripe dedupes — that's the whole point. Rotate
   * only when the operator changes inputs (clearing preview) or runs another
   * op. `useRef` keeps it stable across renders without re-running effects.
   */
  const idempotencyRootRef = useRef<string | null>(null)

  /**
   * Monotonic counter for in-flight preview requests. Bumped on every
   * preview start AND on every context reset; `onSuccess` no-ops if its
   * captured id no longer matches. This stops a slow preview against an
   * old enterprise/cost_center/op from clobbering the screen after the
   * operator has moved on.
   */
  const previewRequestIdRef = useRef(0)

  const operationsQuery = useQuery({
    queryKey: ['billingOps', 'operations'],
    queryFn: async () => (await listBillingOps()).data.operations,
    enabled: isInternalAdmin,
  })

  const costCentersQuery = useQuery({
    queryKey: ['billingOps', 'costCenters', enterprise?.id],
    queryFn: async () => {
      if (!enterprise) throw new Error('missing enterprise')
      return (await listCostCentersForEnterprise(enterprise.id)).data.cost_centers
    },
    enabled: isInternalAdmin && enterprise !== null,
  })

  useEffect(() => {
    if (costCentersQuery.data && costCentersQuery.data.length === 1 && !costCenter) {
      setCostCenter(costCentersQuery.data[0])
    }
  }, [costCentersQuery.data, costCenter])

  const recentInvoicesQuery = useQuery({
    queryKey: ['billingOps', 'recentInvoices', costCenter?.id],
    queryFn: async () => {
      if (!costCenter) throw new Error('missing cost center')
      return (await listRecentInvoicesForCostCenter(costCenter.id)).data
    },
    enabled: isInternalAdmin && costCenter !== null,
  })

  const previewMutation = useMutation({
    mutationFn: async (input: OperationInput) => {
      if (!costCenter || !selectedKind) throw new Error('missing context')
      const requestId = ++previewRequestIdRef.current
      const response = await previewBillingOp({
        cost_center_id: costCenter.id,
        operation_kind: selectedKind,
        input: input as unknown as object,
      })
      return { preview: response.data.preview as unknown as BillingOpsPreviewView, input, requestId }
    },
    onSuccess: ({ preview: result, input, requestId }) => {
      if (requestId !== previewRequestIdRef.current) return
      setPreview(result)
      setPreviewedInput(input)
      setPreviewError(null)
      setCommitError(null)
      // Only mint a key when none is held — re-previewing the same inputs
      // after a commit retry must not invalidate the in-flight idempotency.
      if (idempotencyRootRef.current === null) {
        idempotencyRootRef.current = crypto.randomUUID()
      }
    },
    onError: (error) => {
      setPreview(null)
      setPreviewedInput(null)
      setPreviewError(isApiError(error) ? error.detail || error.message : 'Preview failed')
    },
  })

  const commitMutation = useMutation({
    mutationFn: async () => {
      if (!costCenter || !selectedKind || !previewedInput || !idempotencyRootRef.current) {
        throw new Error('missing commit context')
      }
      const response = await commitBillingOp({
        cost_center_id: costCenter.id,
        operation_kind: selectedKind,
        input: previewedInput as unknown as object,
        idempotency_root: idempotencyRootRef.current,
      })
      return response.data
    },
    onError: (error) => {
      setCommitError(isApiError(error) ? error.detail || error.message : 'Commit failed')
    },
  })

  const resetPreviewAndCommit = () => {
    setPreview(null)
    setPreviewedInput(null)
    setPreviewError(null)
    setCommitError(null)
    commitMutation.reset()
    idempotencyRootRef.current = null
    // Invalidate any in-flight preview's onSuccess so it can't clobber state
    // that now belongs to a different enterprise/cost-center/op.
    previewRequestIdRef.current += 1
  }

  if (!isInternalAdmin) {
    return (
      <div className={styles.container}>
        <p>You are not authorized to access this page</p>
      </div>
    )
  }

  const handleSelectOp = (kind: string) => {
    setSelectedKind(kind)
    resetPreviewAndCommit()
  }

  const handleSelectEnterprise = (selected: EnterpriseListItem) => {
    setEnterprise(selected)
    setCostCenter(null)
    setSelectedKind(null)
    setPickerError(null)
    resetPreviewAndCommit()
  }

  const handleSelectCostCenter = (selected: CostCenterListItem) => {
    setCostCenter(selected)
    setSelectedKind(null)
    resetPreviewAndCommit()
  }

  const handleFormChange = () => {
    if (preview !== null || commitMutation.data || previewError || commitError) {
      resetPreviewAndCommit()
    }
  }

  const handleSubmitInput = (input: OperationInput) => {
    setCommitError(null)
    previewMutation.mutate(input)
  }

  const handleCommit = () => {
    if (selectedKind && ENGINEERING_ONLY_KINDS.has(selectedKind)) {
      const confirmed = window.confirm(
        'ENGINEERING ONLY. This changes which billing system charges this org. Support should not run it. Continue?'
      )
      if (!confirmed) return
    }
    if (preview && preview.reversibility === 'irreversible') {
      const confirmed = window.confirm(
        'This operation is IRREVERSIBLE. Stripe cannot undo it; only a manual correction can. Continue?'
      )
      if (!confirmed) return
    }
    setCommitError(null)
    commitMutation.mutate()
  }

  const handleRunAnother = () => {
    setSelectedKind(null)
    resetPreviewAndCommit()
  }

  const isPending = previewMutation.isPending || commitMutation.isPending
  const showCommitButton = preview !== null && !commitMutation.data
  const committed = commitMutation.data ?? null

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Billing Ops</h1>
      <p className={styles.subtitle}>
        Run pre-defined Stripe corrections against an enterprise. Always preview before committing.
      </p>

      <div className={styles.panel}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>1. Select enterprise</h2>
          <EnterpriseSearch onSelect={handleSelectEnterprise} onError={setPickerError} />
          {pickerError && <div className={styles.errorBanner}>{pickerError}</div>}
          {enterprise && (
            <div className={styles.contextHeader}>
              <span className={styles.contextHeaderLabel}>Selected:</span>
              <span className={styles.contextHeaderValue}>{enterprise.name}</span>
              <span className={styles.contextHeaderId}>{enterprise.id}</span>
            </div>
          )}
        </div>
      </div>

      {enterprise && (
        <div className={styles.panel}>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>2. Select cost center</h2>
            {costCentersQuery.isLoading && <p className={styles.helperText}>Loading cost centers…</p>}
            {costCentersQuery.isError && <div className={styles.errorBanner}>Failed to load cost centers.</div>}
            {costCentersQuery.data && costCentersQuery.data.length === 0 && (
              <div className={styles.errorBanner}>This enterprise has no cost centers.</div>
            )}
            {costCentersQuery.data && costCentersQuery.data.length > 0 && (
              <CostCenterPicker
                costCenters={costCentersQuery.data}
                selectedId={costCenter?.id ?? null}
                onSelect={handleSelectCostCenter}
              />
            )}
            {costCenter && (
              <div className={styles.contextHeader}>
                <span className={styles.contextHeaderLabel}>Selected:</span>
                <span className={styles.contextHeaderValue}>{costCenter.name}</span>
                <span className={styles.contextHeaderId}>{costCenter.id}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {enterprise && costCenter && (
        <div className={styles.panel}>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>3. Select operation</h2>
            {operationsQuery.isLoading && <p className={styles.helperText}>Loading operations…</p>}
            {operationsQuery.isError && <div className={styles.errorBanner}>Failed to load operations.</div>}
            {operationsQuery.data && (
              <OperationList operations={operationsQuery.data} selectedKind={selectedKind} onSelect={handleSelectOp} />
            )}
          </div>
        </div>
      )}

      {enterprise && costCenter && selectedKind && !committed && (
        <div className={styles.panel}>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>4. Provide inputs</h2>
            {selectedKind === OPERATION_KINDS.ADD_PO && (
              <AddPoToInvoiceForm
                disabled={isPending}
                onSubmit={(input: AddPoToInvoiceInput) => handleSubmitInput(input)}
                onChange={handleFormChange}
                formId={FORM_ID}
                draftInvoice={recentInvoicesQuery.data?.draft}
                openInvoice={recentInvoicesQuery.data?.open}
              />
            )}
            {selectedKind === OPERATION_KINDS.SET_DESCRIPTOR && (
              <SetInvoiceStatementDescriptorForm
                disabled={isPending}
                onSubmit={(input: SetInvoiceStatementDescriptorInput) => handleSubmitInput(input)}
                onChange={handleFormChange}
                formId={FORM_ID}
                draftInvoice={recentInvoicesQuery.data?.draft}
                openInvoice={recentInvoicesQuery.data?.open}
              />
            )}
            {selectedKind === OPERATION_KINDS.CONVERT_TO_SALES_OWNED && (
              <ConvertToSalesOwnedForm
                disabled={isPending}
                onSubmit={(input: ConvertToSalesOwnedInput) => handleSubmitInput(input)}
                onChange={handleFormChange}
                formId={FORM_ID}
              />
            )}
            {selectedKind === OPERATION_KINDS.RECREATE_SUB_INVOICE && (
              <RecreateSubscriptionInvoiceForm
                disabled={isPending}
                onSubmit={(input: RecreateSubscriptionInvoiceInput) => handleSubmitInput(input)}
                onChange={handleFormChange}
                formId={FORM_ID}
                openInvoice={recentInvoicesQuery.data?.open}
              />
            )}
            {selectedKind === OPERATION_KINDS.MOVE_TO_NEW_BILLING && (
              <NoInputForm kind={selectedKind} formId={FORM_ID} onSubmit={handleSubmitInput}>
                Preview checks whether this org can move to new billing and lists every reason if it can&apos;t. Nothing
                changes until you commit.
              </NoInputForm>
            )}
            {selectedKind === OPERATION_KINDS.ROLL_BACK_NEW_BILLING_MOVE && (
              <NoInputForm kind={selectedKind} formId={FORM_ID} onSubmit={handleSubmitInput}>
                Preview checks whether this org&apos;s move to new billing can still be undone. Nothing changes until
                you commit.
              </NoInputForm>
            )}
            <div className={styles.actionRow}>
              <button
                type="submit"
                form={FORM_ID}
                disabled={isPending}
                className={styles.opItem}
                style={{ width: 'auto' }}
              >
                <span className={styles.opItemKind}>{previewMutation.isPending ? 'Previewing…' : 'Preview'}</span>
              </button>
            </div>
            {previewError && <div className={styles.errorBanner}>{previewError}</div>}
          </div>
        </div>
      )}

      {preview && !committed && <PreviewPanel preview={preview} />}

      {showCommitButton && (
        <div className={styles.panel}>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>5. Commit</h2>
            <p className={styles.helperText}>
              {selectedKind === OPERATION_KINDS.CONVERT_TO_SALES_OWNED
                ? 'Committing changes only our database; nothing is sent to Stripe. Safe to retry on network errors.'
                : 'Committing will execute the operation against Stripe. Retries reuse the same idempotency key so Stripe dedupes — safe to retry on network errors.'}
            </p>
            <div className={styles.actionRow}>
              <button
                type="button"
                onClick={handleCommit}
                disabled={isPending}
                className={styles.opItem}
                style={{ width: 'auto' }}
              >
                <span className={styles.opItemKind}>{commitMutation.isPending ? 'Committing…' : 'Commit'}</span>
              </button>
            </div>
            {commitError && <div className={styles.errorBanner}>{commitError}</div>}
          </div>
        </div>
      )}

      {committed && <CommitResultPanel result={committed} onRunAnother={handleRunAnother} />}

      <BulkConvertToSalesOwned />
      <BulkMoveToNewBilling />
    </div>
  )
}

export default BillingOpsPage
